/**
 * OpenCVLoader.ts
 * Manages the loading of the OpenCV.js library from a local file.
 * Falls back to CDN if local loading fails.
 */

declare global {
  interface Window {
    cv: any;
    Module: any;
  }
}

export class OpenCVLoader {
  private static instance: OpenCVLoader;
  private isLoaded: boolean = false;
  private loadingPromise: Promise<void> | null = null;

  // Primary: local file (bundled in public/)
  // Fallback: CDN
  private readonly LOCAL_URL = '/opencv.js';
  private readonly CDN_URL = 'https://docs.opencv.org/4.5.5/opencv.js';

  private constructor() {}

  public static getInstance(): OpenCVLoader {
    if (!OpenCVLoader.instance) {
      OpenCVLoader.instance = new OpenCVLoader();
    }
    return OpenCVLoader.instance;
  }

  /**
   * Loads the OpenCV.js script. Tries local first, then CDN fallback.
   * Returns a promise that resolves when cv is ready to use.
   */
  public async load(): Promise<void> {
    if (this.isLoaded) return Promise.resolve();
    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = new Promise((resolve, reject) => {
      // Check if already globally available
      if (window.cv && window.cv.Mat) {
        this.isLoaded = true;
        resolve();
        return;
      }

      const onCVReady = () => {
        // OpenCV.js sets window.cv when loaded via script tag
        const checkReady = () => {
          if (window.cv && window.cv.Mat) {
            console.log('✅ OpenCV.js is ready.');
            this.isLoaded = true;
            resolve();
            return true;
          }
          return false;
        };

        // Sometimes cv needs a moment to initialize
        if (checkReady()) return;

        // Poll for readiness (max 10 seconds)
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (checkReady()) {
            clearInterval(interval);
          } else if (attempts > 100) {
            clearInterval(interval);
            reject(new Error('OpenCV.js yüklendi ancak başlatılamadı.'));
          }
        }, 100);
      };

      // Check if script is already present in DOM (e.g. loaded via index.html)
      const existingScript = document.querySelector('script[src*="opencv.js"]');
      if (existingScript) {
        console.log('OpenCV.js script tag already exists in DOM, waiting for it to be ready...');
        onCVReady();
        return;
      }

      // Configure Module for OpenCV WASM initialization
      window.Module = {
        onRuntimeInitialized: () => {
          console.log('OpenCV WASM runtime initialized.');
          onCVReady();
        }
      };

      const loadScript = (url: string, onError: () => void) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => {
          console.log(`OpenCV script loaded from: ${url}`);
          // For some builds, onRuntimeInitialized might not fire
          // So we also check after a delay
          setTimeout(() => {
            if (!this.isLoaded) onCVReady();
          }, 2000);
        };
        script.onerror = onError;
        document.body.appendChild(script);
      };

      // Try local first
      console.log('Loading OpenCV.js from local...');
      loadScript(this.LOCAL_URL, () => {
        console.warn('Local OpenCV.js failed, trying CDN fallback...');
        loadScript(this.CDN_URL, () => {
          this.loadingPromise = null;
          reject(new Error('OpenCV.js yüklenemedi. Lütfen internet bağlantınızı kontrol edin.'));
        });
      });
    });

    return this.loadingPromise;
  }

  public getCV(): any {
    if (!this.isLoaded || !window.cv) {
      throw new Error('OpenCV.js not loaded. Call load() first and wait for resolution.');
    }
    return window.cv;
  }

  public isReady(): boolean {
    return this.isLoaded;
  }
}

export const openCVLoader = OpenCVLoader.getInstance();
